export const LOCATIONS = ["Costco", "Don Quijote", "Drugstore", "Department Store", "Online"];

export const REGIONS = {
  Europe: { code: "EUR", symbol: "€" },
  Japan: { code: "JPY", symbol: "¥" },
  Korea: { code: "KRW", symbol: "₩" },
  "Hong Kong": { code: "HKD", symbol: "HK$" },
  Australia: { code: "AUD", symbol: "A$" },
};

export const fmtNum = (v) => Number(Number(v).toFixed(2)).toLocaleString("en-US");
export const fmtIDR = (v) => `Rp${Number(Number(v).toFixed(2)).toLocaleString("id-ID")}`;
export const fmtIDRInput = (raw) => (raw || "").replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
// Global numeric input formatting — raw state stays digits; display is grouped
export const fmtQtyInput = (raw) => String(raw ?? "").replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
export const moneyDigits = (raw, signed = false) => {
  const s = String(raw ?? "");
  const neg = signed && s.replace(/\s/g, "").startsWith("-");
  return (neg ? "-" : "") + s.replace(/\D/g, "");
};
export const fmtMoneyInput = (raw, signed = false) => {
  const neg = signed && String(raw ?? "").startsWith("-");
  return (neg ? "-" : "") + fmtQtyInput(raw);
};
// Decimal-capable input: integer part grouped with dots, decimals preserved after a comma (display-only)
export const fmtDecInput = (raw) => {
  const s = String(raw ?? "");
  const neg = s.startsWith("-");
  const body = neg ? s.slice(1) : s;
  const dot = body.indexOf(".");
  const intPart = (dot === -1 ? body : body.slice(0, dot)).replace(/\D/g, "");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return (neg ? "-" : "") + grouped + (dot !== -1 ? "," + body.slice(dot + 1).replace(/\D/g, "") : "");
};
// Parse a formatted decimal display back to raw digits with "." decimal point
export const decDigits = (display, e) => {
  let v = String(display ?? "");
  const it = e?.nativeEvent?.inputType;
  if (it === "insertText" && (e.nativeEvent.data === "." || e.nativeEvent.data === ",")) {
    const pos = e.target.selectionStart - 1; // a separator the user just typed is always a decimal intent
    v = v.slice(0, pos) + "," + v.slice(pos + 1);
  }
  const neg = v.trim().startsWith("-");
  v = v.replace(/-/g, "");
  if (v.includes(",")) {
    v = v.replace(/\./g, "").replace(/,/g, ".");
  } else if (!it || (it.startsWith("insert") && it !== "insertText")) {
    // paste / programmatic fill: a single dot with != 3 trailing digits is a decimal, else thousands
    const parts = v.split(".");
    v = parts.length === 2 && parts[1].length !== 3 ? parts[0].replace(/\D/g, "") + "." + parts[1].replace(/\D/g, "") : v.replace(/\./g, "");
  } else {
    v = v.replace(/\./g, "");
  }
  v = v.replace(/[^\d.]/g, "");
  const i = v.indexOf(".");
  if (i !== -1) v = v.slice(0, i + 1) + v.slice(i + 1).replace(/\./g, "");
  return (neg ? "-" : "") + v;
};
export const fmtLocal = (v, symbol) => `${symbol || ""}${fmtNum(v)}`;
export const fmtRate = (symbol, rate) => `${symbol || ""}1 = ${fmtIDR(rate)}`;

export const ROLE_LABELS = {
  owner: "Owner",
  purchaser: "Purchaser",
  admin: "Admin Checker (legacy)",
  receiving: "Admin Stock / Receiving PIC",
  viewer: "View Only (legacy)",
  admin_input: "Admin Input / Checker",
};

export const CUSTOMER_SOURCES = ["Instagram", "WhatsApp", "Shopee", "Tokopedia", "Offline", "Other"];

export const ORDER_STATUS_META = {
  active: { label: "ACTIVE", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "CANCELLED", badge: "bg-red-50 text-red-600 border-red-200" },
  completed: { label: "COMPLETED", badge: "bg-indigo-50 text-indigo-700 border-indigo-200" },
};

export const DESTINATIONS = ["Japan", "USA", "Europe", "Korea", "Hong Kong", "Australia"];

export const SESSION_STATUS_META = {
  draft: { label: "DRAFT", badge: "border-amber-200 bg-amber-50 text-amber-700" },
  pending_approval: { label: "PENDING CHECKER APPROVAL", badge: "border-indigo-200 bg-indigo-50 text-indigo-700" },
  correction_required: { label: "CORRECTION REQUIRED", badge: "border-red-200 bg-red-50 text-red-700" },
  approved: { label: "APPROVED", badge: "border-emerald-200 bg-emerald-50 text-emerald-700" },
};

export const RECEIVING_STATUS_META = {
  waiting: { label: "WAITING TO RECEIVE", badge: "bg-amber-50 text-amber-700 border-amber-200" },
  partially_received: { label: "PARTIALLY RECEIVED", badge: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  fully_received: { label: "FULLY RECEIVED", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export const BATCH_STATUS_META = {
  open: { label: "OPEN", badge: "border-slate-200 bg-slate-100 text-slate-600" },
  purchasing: { label: "PURCHASING", badge: "border-indigo-200 bg-indigo-50 text-indigo-700" },
  receiving: { label: "RECEIVING", badge: "border-amber-200 bg-amber-50 text-amber-700" },
  ready_to_close: { label: "READY TO CLOSE", badge: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  closed: { label: "CLOSED", badge: "border-slate-500 bg-slate-700 text-white" },
};

export const STATUS_META = {
  not_purchased: { label: "Not Purchased", badge: "bg-slate-100 text-slate-600 border-slate-200", bar: "#94A3B8" },
  partial: { label: "Partial", badge: "bg-amber-50 text-amber-700 border-amber-200", bar: "#F59E0B" },
  purchased: { label: "Purchased", badge: "bg-emerald-50 text-emerald-700 border-emerald-200", bar: "#10B981" },
};

export const PACKING_STATUS_META = {
  packing: { label: "PACKING", badge: "bg-amber-50 text-amber-700 border-amber-200" },
  packed: { label: "PACKED", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export const SHIPMENT_STATUS_META = {
  preparing: { label: "PREPARING", badge: "bg-amber-50 text-amber-700 border-amber-200" },
  shipped: { label: "SHIPPED", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export const PAYMENT_STATUS_META = {
  recorded: { label: "RECORDED — FINANCE CHECKED EXTERNALLY", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending: { label: "PENDING", badge: "bg-amber-50 text-amber-700 border-amber-200" },
  verified: { label: "VERIFIED", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "REJECTED", badge: "bg-red-50 text-red-600 border-red-200" },
  voided: { label: "VOIDED", badge: "bg-slate-100 text-slate-500 border-slate-300" },
};

export const PAYMENT_METHODS = ["Transfer BCA", "Transfer CIMB", "QRIS"];

export const PAYMENT_TYPE_LABELS = {
  DP: "DP / Down Payment",
  ADDITIONAL: "Additional Payment",
  SETTLEMENT: "Full Settlement",
};

export const REFUND_STATUS_META = {
  pending: { label: "PENDING", badge: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "APPROVED", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "REJECTED", badge: "bg-red-50 text-red-600 border-red-200" },
};

export const SHIPPING_QUEUE_META = {
  ready: { label: "READY TO SHIP", badge: "bg-amber-50 text-amber-700 border-amber-200" },
  prepared: { label: "SHIPPING PREPARED", badge: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  shipped: { label: "SHIPPED", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export const SHIPMENT_EVENT_LABELS = {
  shipment_created: "Shipping record created",
  details_saved: "Shipping details saved",
  shipment_updated: "Shipping details updated",
  charge_set: "Shipping cost set",
  shipped: "Marked as shipped",
  reopened: "Reopened by Owner",
  unpaid_override: "Ship-before-payment authorized",
};
